import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { contentService } from '../services/contentService';
import { queryKeys } from '../api/queryClient';
import {
  patchFeedCaches,
  syncArticleReactionCaches,
  syncArticleShareCaches,
} from '../utils/articleReactions';
import { useAuth } from './useAuth';
import { useDismissableError } from './useDismissableError';

function getOrCreateViewSessionId() {
  try {
    const key = 'prasaar_view_session';
    let value = sessionStorage.getItem(key);
    if (!value) {
      value = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(key, value);
    }
    return value;
  } catch {
    return undefined;
  }
}

export function useArticle(id) {
  const { applyViewer, user, hydrating } = useAuth();
  const queryClient = useQueryClient();
  const articleKey = queryKeys.article(id, user?.id ?? null);
  const viewedRef = useRef(null);

  const query = useQuery({
    queryKey: articleKey,
    queryFn: async () => {
      const data = await pagesService.article(id);
      if (data?.viewer) applyViewer(data.viewer);
      return data;
    },
    enabled: Boolean(id) && !hydrating,
  });

  useEffect(() => {
    if (!id || !query.data?.article) return;
    if (viewedRef.current === id) return;
    viewedRef.current = id;
    contentService
      .view(id, { sessionId: getOrCreateViewSessionId() })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
      })
      .catch(() => {
        /* non-blocking */
      });
  }, [id, query.data?.article, queryClient]);

  const reactMutation = useMutation({
    mutationFn: (emoji) => contentService.react(id, emoji),
    onSuccess: (res) => {
      syncArticleReactionCaches(queryClient, id, res);
      void queryClient.invalidateQueries({ queryKey: articleKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => contentService.share(id),
    onSuccess: (res) => {
      syncArticleShareCaches(queryClient, id, res.shareCount);
      void queryClient.invalidateQueries({ queryKey: articleKey });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
    },
  });

  const commentMutation = useMutation({
    mutationFn: (text) => contentService.comment(id, text),
    onSuccess: (comment) => {
      queryClient.setQueryData(articleKey, (prev) => {
        if (!prev?.article) return prev;
        const commentCount = (prev.article.commentCount ?? 0) + 1;
        patchFeedCaches(queryClient, id, { commentCount });
        return {
          ...prev,
          article: {
            ...prev.article,
            comments: [comment, ...(prev.article.comments ?? [])],
            commentCount,
          },
        };
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId) => contentService.deleteComment(id, commentId),
    onSuccess: (_res, commentId) => {
      queryClient.setQueryData(articleKey, (prev) => {
        if (!prev?.article) return prev;
        const comments = (prev.article.comments ?? []).filter(
          (c) => c.id !== commentId,
        );
        const commentCount = Math.max(0, (prev.article.commentCount ?? 1) - 1);
        patchFeedCaches(queryClient, id, { commentCount });
        return {
          ...prev,
          article: {
            ...prev.article,
            comments,
            commentCount,
          },
        };
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'analytics'] });
    },
  });

  const react = useCallback(
    async (emoji) => {
      return reactMutation.mutateAsync(emoji);
    },
    [reactMutation],
  );

  const like = useCallback(async () => {
    return react('👍');
  }, [react]);

  const share = useCallback(async () => {
    return shareMutation.mutateAsync();
  }, [shareMutation]);

  const comment = useCallback(
    async (text) => {
      return commentMutation.mutateAsync(text);
    },
    [commentMutation],
  );

  const deleteComment = useCallback(
    async (commentId) => {
      return deleteCommentMutation.mutateAsync(commentId);
    },
    [deleteCommentMutation],
  );

  const { error, setError } = useDismissableError(
    query.error ||
      reactMutation.error ||
      shareMutation.error ||
      commentMutation.error ||
      deleteCommentMutation.error,
  );

  return {
    article: query.isError ? null : query.data?.article ?? null,
    loading: hydrating || query.isLoading,
    error,
    busy:
      reactMutation.isPending ||
      shareMutation.isPending ||
      commentMutation.isPending ||
      deleteCommentMutation.isPending,
    setError,
    load: () => query.refetch(),
    like,
    react,
    share,
    comment,
    deleteComment,
  };
}
